import { useState } from 'react';
import { CircleAlert, PackageCheck, ShoppingBag } from 'lucide-react';

import { deliveriesApi } from '@/api/deliveries.api';
import type { BatchPickupResult, DeliveryJob } from '@/api/types';
import { SHOW_HANDOVER_CODE } from '@/lib/env';
import { errorMessage } from '@/lib/errors';
import { formatMoney } from '@/lib/format';
import { isCashOnDelivery } from '@/lib/status';
import { Button } from './ui/button';
import { Card, CardBody } from './ui/card';
import { Sheet, SheetContent } from './ui/sheet';

/**
 * Collecting several bags at one counter, in one action.
 *
 * A rider sent to a shop for three orders makes one trip, and doing it a job
 * at a time is three screens and three round trips while a pharmacist waits.
 *
 * The result is deliberately NOT all-or-nothing, and this sheet has to show
 * both halves of it. A bag that was not packed yet is left on the counter, and
 * a rider who rode off believing they had three when they have two is the
 * exact failure this is meant to prevent — so what was skipped is listed as
 * prominently as what was collected.
 */
export function BatchPickupSheet({
  jobs,
  open,
  onOpenChange,
  onCollected,
}: {
  /** Every bag packed and waiting at this counter, the one on screen included. */
  jobs: DeliveryJob[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCollected: () => void;
}) {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BatchPickupResult | null>(null);

  const shop = jobs[0]?.pharmacyName ?? 'this pharmacy';
  const cash = jobs
    .filter(isCashOnDelivery)
    .reduce((total, job) => total + (Number.parseFloat(job.codAmount ?? '0') || 0), 0);

  function close(next: boolean) {
    onOpenChange(next);
    if (!next) {
      // Cleared on the way out rather than the way in, so the summary stays
      // readable for as long as the sheet is open.
      setResult(null);
      setError(null);
    }
  }

  async function collect() {
    if (working) return;
    setWorking(true);
    setError(null);
    try {
      const answer = await deliveriesApi.pickUpMany(jobs.map((job) => job.id));
      setResult(answer);
      // The run sheet and every affected delivery are now stale regardless of
      // how the batch went — even a fully skipped batch may have learnt that a
      // leg moved on.
      onCollected();
    } catch (batchError) {
      setError(errorMessage(batchError));
    } finally {
      setWorking(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={close}>
      <SheetContent
        title={result ? 'What you are carrying' : `Collect ${jobs.length} bags`}
        description={
          result
            ? undefined
            : `All ${jobs.length} of these are packed and waiting at ${shop}.`
        }
      >
        {result ? (
          <Outcome result={result} onDone={() => close(false)} />
        ) : (
          <div className="grid gap-4 pb-2">
            <ul className="grid gap-2">
              {jobs.map((job) => (
                <li key={job.id}>
                  <Card variant="sunken">
                    <CardBody className="flex items-center gap-3 py-3">
                      <PackageCheck
                        className="size-4 shrink-0 text-success-ink"
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1 truncate font-semibold text-ink">
                        {job.orderNumber ?? 'Order'}
                      </span>
                      <span className="shrink-0 truncate text-sm text-ink-muted">
                        {job.customerName ?? 'Customer'}
                      </span>
                    </CardBody>
                  </Card>
                </li>
              ))}
            </ul>

            {cash > 0 ? (
              <p
                data-numeric
                className="rounded-lg border border-error-line/60 bg-error-surface px-4 py-3 text-sm font-semibold leading-snug text-error-ink"
              >
                {formatMoney(cash.toFixed(2))} to collect in cash across these orders.
              </p>
            ) : null}

            {error ? (
              <p role="alert" className="text-sm font-medium text-error-ink">
                {error}
              </p>
            ) : null}

            <Button
              size="xl"
              block
              loading={working}
              loadingLabel="Collecting"
              onClick={() => void collect()}
            >
              <ShoppingBag aria-hidden="true" />
              I have all {jobs.length} bags
            </Button>

            <Button variant="ghost" size="md" block onClick={() => close(false)}>
              Collect them one at a time
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/**
 * What actually happened, both halves of it.
 *
 * The skipped list is not an error state and is not hidden behind one: it is
 * the answer to "which bags am I still waiting on", which is the question the
 * rider has while standing at the counter.
 */
function Outcome({ result, onDone }: { result: BatchPickupResult; onDone: () => void }) {
  return (
    <div className="grid gap-4 pb-2">
      {result.collected.length > 0 ? (
        <section aria-labelledby="collected-heading">
          <h3 id="collected-heading" className="eyebrow pb-2">
            In your bag ({result.collected.length})
          </h3>
          <ul className="grid gap-2">
            {result.collected.map((entry) => (
              <li key={entry.deliveryId}>
                <Card variant="wash">
                  <CardBody className="flex items-center gap-3 py-3">
                    <PackageCheck className="size-4 shrink-0 text-brand" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate font-semibold text-ink">
                      {entry.orderNumber ?? 'Order'}
                    </span>
                    {/*
                     * The handover code, in DEVELOPMENT BUILDS ONLY, under the
                     * same rule as the single pickup: it is the customer's
                     * proof, not the rider's, and it disappears from release
                     * the moment SMS is wired up. See lib/env.ts.
                     */}
                    {SHOW_HANDOVER_CODE ? (
                      <span
                        data-numeric
                        className="shrink-0 font-bold tracking-widest text-brand"
                        aria-label={`Handover code ${entry.customerOtp.split('').join(' ')}`}
                      >
                        {entry.customerOtp}
                      </span>
                    ) : null}
                  </CardBody>
                </Card>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {result.skipped.length > 0 ? (
        <section aria-labelledby="skipped-heading">
          <h3 id="skipped-heading" className="eyebrow pb-2">
            Left on the counter ({result.skipped.length})
          </h3>
          <ul className="grid gap-2">
            {result.skipped.map((entry) => (
              <li key={entry.deliveryId}>
                <Card variant="warning">
                  <CardBody className="flex items-start gap-3 py-3">
                    <CircleAlert
                      className="mt-0.5 size-4 shrink-0 text-warning-ink"
                      aria-hidden="true"
                    />
                    {/* The server's own sentence — it names what to wait for. */}
                    <span className="min-w-0 flex-1 text-sm leading-snug text-ink [overflow-wrap:anywhere]">
                      {entry.message}
                    </span>
                  </CardBody>
                </Card>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {SHOW_HANDOVER_CODE && result.collected.length > 0 ? (
        <p className="rounded-lg border border-warning-line/60 bg-warning-surface px-4 py-3 text-sm leading-snug text-ink-muted">
          Development build: no SMS provider is wired up, so read each code out at the door. Write
          them down — they are not shown again.
        </p>
      ) : null}

      <Button size="lg" block onClick={onDone}>
        Done
      </Button>
    </div>
  );
}
